import type { DecodedBrowserAppointment } from './types';

export type MemberCoachBookingState =
  'no_previous' | 'upcoming_chemistry' | 'has_previous' | 'active_biweekly';

export interface MemberCoachBookingAuthority {
  appointmentState: 'ready' | 'refreshing' | 'unavailable';
  appointments: readonly DecodedBrowserAppointment[];
  coachPersonId: string | null;
  identityState: 'ready' | 'refreshing' | 'unavailable';
  matchState: 'current' | 'unmatched' | 'unavailable';
}

export interface MemberCoachBookingDecision {
  canBookBiweekly: boolean;
}

export function legacySubjectPersonId(
  value: { subject?: { personId?: string } } | undefined,
): string | null {
  return value?.subject?.personId ?? null;
}

export interface QueryAuthorityInput {
  hasCurrentData: boolean;
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
}

export function resolveQueryAuthorityState({
  hasCurrentData,
  isError,
  isFetching,
  isSuccess,
}: QueryAuthorityInput): 'ready' | 'refreshing' | 'unavailable' {
  if (isFetching) return 'refreshing';
  if (isError || !isSuccess || !hasCurrentData) return 'unavailable';
  return 'ready';
}

interface MatchAuthorityInput extends QueryAuthorityInput {
  currentCoachIds: readonly string[];
}

export function resolveMemberCoachMatchState(
  authority: MatchAuthorityInput,
  coachId: string,
): MemberCoachBookingAuthority['matchState'] {
  if (resolveQueryAuthorityState(authority) !== 'ready') return 'unavailable';
  if (authority.currentCoachIds.includes(coachId)) return 'current';
  return 'unmatched';
}

export function resolveMemberCoachBookingAuthority(
  authority: MemberCoachBookingAuthority,
  now: number,
): MemberCoachBookingDecision {
  if (
    authority.matchState !== 'current' ||
    authority.appointmentState !== 'ready' ||
    authority.identityState !== 'ready' ||
    !authority.coachPersonId
  ) {
    return { canBookBiweekly: false };
  }

  const hasLiveFutureCoaching = authority.appointments.some((appointment) => {
    if (
      appointment.expectedIdentities.coach.personId !==
        authority.coachPersonId ||
      appointment.kind !== 'coaching' ||
      appointment.seriesId === null ||
      appointment.bookingStatus === 'rejected' ||
      appointment.lifecycleStatus === 'cancelled'
    ) {
      return false;
    }
    const endAt = Date.parse(appointment.currentSchedule.endAt);
    return !Number.isFinite(endAt) || endAt >= now;
  });

  return { canBookBiweekly: !hasLiveFutureCoaching };
}

export interface ResolveMemberCoachBookingStateInput {
  legacyState: MemberCoachBookingState;
  appointments: readonly DecodedBrowserAppointment[];
  coachPersonId?: string;
  hasAuthoritativeAppointments: boolean;
  isCoachIdentityFetching: boolean;
  now: number;
}

export function resolveMemberCoachBookingState({
  appointments,
  coachPersonId,
  hasAuthoritativeAppointments,
  isCoachIdentityFetching,
  legacyState,
  now,
}: ResolveMemberCoachBookingStateInput): MemberCoachBookingState {
  if (isCoachIdentityFetching || !coachPersonId) {
    return legacyState;
  }

  const isMatchingCoach = (appointment: DecodedBrowserAppointment) =>
    appointment.expectedIdentities.coach.personId === coachPersonId;
  const hasChemistryClaimStatus = (appointment: DecodedBrowserAppointment) =>
    appointment.bookingStatus === 'pending' ||
    appointment.bookingStatus === 'confirmed';
  const hasAuthoritativeChemistryCancellation = appointments.some(
    (appointment) =>
      hasChemistryClaimStatus(appointment) &&
      appointment.kind === 'chemistry' &&
      appointment.lifecycleStatus === 'cancelled' &&
      isMatchingCoach(appointment),
  );
  const matchingChemistryClaims = appointments.filter(
    (appointment) =>
      hasChemistryClaimStatus(appointment) &&
      appointment.kind === 'chemistry' &&
      appointment.lifecycleStatus !== 'cancelled' &&
      isMatchingCoach(appointment),
  );
  const hasEnded = (appointment: DecodedBrowserAppointment) => {
    const endAt = Date.parse(appointment.currentSchedule.endAt);
    return Number.isFinite(endAt) && endAt < now;
  };

  if (
    appointments.some(
      (appointment) =>
        appointment.kind === 'coaching' &&
        appointment.seriesId !== null &&
        appointment.lifecycleStatus !== 'cancelled' &&
        isMatchingCoach(appointment) &&
        (appointment.bookingStatus !== 'confirmed' || !hasEnded(appointment)),
    )
  ) {
    return 'active_biweekly';
  }
  if (
    matchingChemistryClaims.some(
      (appointment) =>
        appointment.bookingStatus === 'pending' || !hasEnded(appointment),
    )
  ) {
    return 'upcoming_chemistry';
  }
  if (!hasAuthoritativeAppointments) return legacyState;
  if (
    matchingChemistryClaims.some(
      (appointment) =>
        appointment.bookingStatus === 'confirmed' && hasEnded(appointment),
    )
  ) {
    return 'has_previous';
  }
  if (hasAuthoritativeChemistryCancellation) return 'no_previous';
  if (legacyState === 'has_previous') return 'has_previous';
  if (legacyState === 'upcoming_chemistry') {
    return 'upcoming_chemistry';
  }

  return 'no_previous';
}
